import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from 'react-query';
import { CerebroTextButton } from '../../../shared/components';
import { useApi } from '../../../shared/context';
import { QueryKeys } from '../../../shared/hooks';
import { TermSheet, TermSheetRevision } from '../../../shared/models';
import { CerebroApiEndpoints } from '../../../shared/utils/api';
import { getResponseData } from '../../../shared/utils/axios';

interface DeleteRevisionDialogProps {
	dataRoomGuid: string | undefined;
	sheet: TermSheet | null;
	revision: TermSheetRevision | null;
	close: () => void;
}

export const DeleteRevisionDialog: React.FC<DeleteRevisionDialogProps> = ({ dataRoomGuid, sheet, revision, close }) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();

	const onDeleteConfirmation = () => {
		if (revision !== null) deleteRevision(revision);
	};

	const { mutate: deleteRevision } = useMutation<void, unknown, TermSheetRevision>(
		revisionToDelete =>
			getResponseData(
				api.delete<void>(
					CerebroApiEndpoints.TermSheets.Revision(dataRoomGuid, sheet?.guid, revisionToDelete.guid)
				)
			),
		{
			onMutate: () => {
				close();
			},
			onSuccess: () => {
				client.invalidateQueries(QueryKeys.TermSheets.List(dataRoomGuid));
				client.invalidateQueries(QueryKeys.Lookups.ActiveDataRoomLenders(dataRoomGuid));
				snackbar.enqueueSnackbar('Term Sheet Revision successfully deleted.', { variant: 'success' });
				close();
			},
			onError: () => {
				snackbar.enqueueSnackbar('There was a problem deleting the term sheet revision.', { variant: 'error' });
			},
		}
	);

	return (
		<Dialog open={revision !== null} onClose={close}>
			<DialogTitle>Confirm Delete Term Sheet Revision</DialogTitle>
			<DialogContent>
				<DialogContentText>
					Are you sure you want to delete {sheet?.lenderName} at {sheet?.bankName} revision number{' '}
					{revision?.versionNumber}?
				</DialogContentText>
			</DialogContent>
			<DialogActions>
				<CerebroTextButton onClick={close}>No</CerebroTextButton>
				<CerebroTextButton onClick={onDeleteConfirmation}>Yes</CerebroTextButton>
			</DialogActions>
		</Dialog>
	);
};
